import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IWatrReimbursement, IWatrRequest, IWageAndTravelVerifyValidation, IPaystub } from '@nkr/models/index';
import { FileDocumentType } from '@nkr/enums/document-type';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { DonorShieldContext } from '../context';
import WatrInfoAccordions from '../accordion';
import Header from '../header';
import VerificationStepTracker from '../verification-step-tracker';
import { parseMoneyInput } from '../common/money-input';
import { isHourly, isSelfEmployed } from '../utils/employment-type.utils';
import ConnectedDevMockDocsOffer from '../dev-mock-docs/ConnectedDevMockDocsOffer';
import SelfEmployedIncomeSection from './components/SelfEmployedIncomeSection';
import WagePaystubIncomeSection from './components/WagePaystubIncomeSection';
import {
    buildPaystubViewerDocs,
    buildSelfEmployedViewerDocs,
    calculatePaystubOverview,
    getPaystubVerifyDates,
    getSelfEmployedVerifyPayload,
    sortPaystubs
} from './utils/verify-stub.utils';

interface IProps {
    data: IWatrRequest,
    isAudit: boolean,
    reimbursements: IWatrReimbursement[],
    validation: IWageAndTravelVerifyValidation,
    isDirty: boolean
}

const VerifyStub = (props: IProps) => {
    const [paystubs, setPaystubs] = useState<IPaystub[]>([]);
    const [draftOtherAmounts, setDraftOtherAmounts] = useState<Record<number, number>>({});
    const [grossFocusIndex, setGrossFocusIndex] = useState<number | null>(null);
    const [viewingIndex, setViewingIndex] = useState(0);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [approvedPaystubIds, setApprovedPaystubIds] = useState<Set<number>>(new Set());
    const [taxYear, setTaxYear] = useState(Number(props.data.incomeIrsYear) || 0);
    const [taxGross, setTaxGross] = useState(Number(props.data.incomeIrsYearGross) || 0);
    const fileLoadAttemptedForRequestId = useRef<number | null>(null);

    const context = useContext(DonorShieldContext);

    const employmentType = props.data.employmentType;
    const isHourlyValue = isHourly(employmentType);
    const isSelfEmployedValue = isSelfEmployed(employmentType);

    const hasHeaderPayStubSummary =
        (props.data.incomePayStubGross != null && Number(props.data.incomePayStubGross) > 0) ||
        (props.data.incomePayStubStartDate != null && String(props.data.incomePayStubStartDate).length > 0) ||
        (props.data.incomePayStubEndDate != null && String(props.data.incomePayStubEndDate).length > 0);

    const filteredWeeks = props.data.requestWeeks?.filter(w =>
        w.status > WageAndTravelReimbursementRequestStatus.PendingApproval
        && w.status < WageAndTravelReimbursementRequestStatus.CenterDeclined
    ) || [];

    const persistDraftOtherAmounts = async () => {
        if (!context.onUpdateRequestWeekOtherAmount) {
            return;
        }

        const updates = filteredWeeks.filter(w => {
            if ((w.reimbursementId || 0) > 0) {
                return false;
            }

            const newAmount = draftOtherAmounts[w.weekRequested] ?? w.otherAmount ?? 0;
            const currentAmount = w.otherAmount ?? 0;
            return newAmount !== currentAmount;
        });

        await Promise.all(updates.map(w =>
            context.onUpdateRequestWeekOtherAmount(props.data.id, w.weekRequested, draftOtherAmounts[w.weekRequested] ?? w.otherAmount ?? 0)
        ));
    };

    const sourcePaystubs = props.data.paystubs && Array.isArray(props.data.paystubs)
        ? props.data.paystubs
        : [];

    const sortedPaystubs = useMemo(() => sortPaystubs(sourcePaystubs), [sourcePaystubs]);

    const viewerDocs = useMemo(() => {
        if (isSelfEmployedValue) {
            return buildSelfEmployedViewerDocs(props.data);
        }

        return buildPaystubViewerDocs(sortedPaystubs, props.data.id);
    }, [
        isSelfEmployedValue,
        sortedPaystubs,
        props.data
    ]);

    const paystubFileIds = useMemo(
        () => (paystubs.length > 0 ? paystubs : sortedPaystubs)
            .map(paystub => paystub.id)
            .filter((id): id is number => id != null),
        [paystubs, sortedPaystubs]
    );

    const paystubFileMap = useMemo(() => {
        const map = new Map<number, any[]>();
        const rows = paystubs.length > 0 ? paystubs : sortedPaystubs;

        rows.forEach((paystub) => {
            if (paystub.id == null) {
                return;
            }

            map.set(paystub.id, [{
                id: paystub.id,
                amount: paystub.grossAmount,
                expenseDate: paystub.startDate,
                endDate: paystub.endDate,
                reviewRequired: paystub.reviewRequired === true,
                approved: approvedPaystubIds.has(paystub.id),
                expenseType: 0
            }]);
        });

        return map;
    }, [paystubs, sortedPaystubs, approvedPaystubIds]);

    const markPaystubsApproved = useCallback((paystubIds: number[], approved: boolean) => {
        setApprovedPaystubIds(prev => {
            const next = new Set(prev);

            paystubIds.forEach(id => {
                if (approved) {
                    next.add(id);
                } else {
                    next.delete(id);
                }
            });

            return next;
        });
    }, []);

    const applyReviewRequired = useCallback(async (paystubIds: number[], reviewRequired: boolean) => {
        setPaystubs(prev => prev.map(item =>
            paystubIds.includes(item.id) ? { ...item, reviewRequired } : item
        ));
        markPaystubsApproved(paystubIds, !reviewRequired);

        if (!context.onSetPaystubReviewRequired) {
            return;
        }

        await Promise.all(paystubIds.map(id => context.onSetPaystubReviewRequired!(id, reviewRequired)));
    }, [context, markPaystubsApproved]);

    const openViewerAtIndex = useCallback(async (index: number) => {
        if (!viewerDocs.length || index < 0 || index >= viewerDocs.length) {
            return;
        }

        const doc = viewerDocs[index];

        try {
            const fileUrl = await context.fetchFileUrl(doc.docType, doc.lookupId);

            if (!fileUrl) {
                return;
            }

            setViewingIndex(index);

            if (isSelfEmployedValue) {
                context.onShowDataSideBar(
                    doc.docType,
                    doc.lookupId,
                    fileUrl,
                    viewerDocs.length,
                    undefined,
                    index,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    viewerDocs,
                    (_docType, nextIndex) => setViewingIndex(nextIndex)
                );
                return;
            }

            context.onShowDataSideBar(
                doc.docType,
                doc.lookupId,
                fileUrl,
                paystubFileIds.length,
                paystubFileIds,
                index,
                paystubFileMap,
                async (paystubIds) => {
                    await applyReviewRequired(paystubIds, false);
                },
                async (paystubIds) => {
                    await applyReviewRequired(paystubIds, true);
                },
                undefined,
                undefined,
                undefined,
                (paystubIds, reviewRequired) => {
                    setPaystubs(prev => prev.map(item =>
                        paystubIds.includes(item.id) ? { ...item, reviewRequired } : item
                    ));
                    markPaystubsApproved(paystubIds, !reviewRequired);
                },
                viewerDocs,
                (_docType, nextIndex) => setViewingIndex(nextIndex)
            );
        } catch {
        }
    }, [
        viewerDocs,
        context,
        isSelfEmployedValue,
        paystubFileIds,
        paystubFileMap,
        applyReviewRequired,
        markPaystubsApproved
    ]);

    useEffect(() => {
        if (props.data.paystubs && Array.isArray(props.data.paystubs) && props.data.paystubs.length > 0) {
            setPaystubs([...props.data.paystubs]);
        } else {
            setPaystubs([]);
        }

        setEditingIndex(null);
        setTaxYear(Number(props.data.incomeIrsYear) || 0);
        setTaxGross(Number(props.data.incomeIrsYearGross) || 0);
    }, [props.data]);

    useEffect(() => {
        setApprovedPaystubIds(new Set());
        setViewingIndex(0);
    }, [props.data.id]);

    useEffect(() => {
        const requestId = props.data.id;

        if (fileLoadAttemptedForRequestId.current === requestId) {
            return;
        }

        if (!viewerDocs.length) {
            return;
        }

        fileLoadAttemptedForRequestId.current = requestId;
        openViewerAtIndex(0);
    }, [props.data.id, viewerDocs, openViewerAtIndex]);

    const isPaystubViewing = (paystub: IPaystub) => {
        const viewed = sortedPaystubs[viewingIndex];

        return viewed != null && paystub.id != null && viewed.id === paystub.id;
    };

    const handleReviewRequiredChange = async (paystub: IPaystub, checked: boolean) => {
        if (!paystub.id) {
            return;
        }

        setPaystubs(prev => prev.map(item =>
            item.id === paystub.id ? { ...item, reviewRequired: checked } : item
        ));

        if (checked) {
            markPaystubsApproved([paystub.id], false);
        }

        if (context.onSetPaystubReviewRequired) {
            try {
                await context.onSetPaystubReviewRequired(paystub.id, checked);
            } catch {
            }
        }
    };

    const handleViewPaystubRow = async (paystub: IPaystub) => {
        const index = sortedPaystubs.findIndex(item => item.id === paystub.id);

        if (index >= 0) {
            await openViewerAtIndex(index);
        }
    };

    const handleModifyClick = (index: number, isEditing: boolean) => {
        setEditingIndex(isEditing ? null : index);
    };

    const handleRowClick = (event: React.MouseEvent, paystub: IPaystub) => {
        const target = event.target as HTMLElement;

        if (target.closest('input, button, a, select, textarea, label')) {
            return;
        }

        handleViewPaystubRow(paystub);
    };

    const handlePaystubChange = (index: number, field: 'startDate' | 'endDate' | 'grossAmount', value: string | number) => {
        const updatedPaystubs = [...paystubs];

        if (field === 'grossAmount') {
            const amount = typeof value === 'number' ? value : parseMoneyInput(String(value));
            updatedPaystubs[index] = { ...updatedPaystubs[index], grossAmount: amount };
        } else {
            updatedPaystubs[index] = { ...updatedPaystubs[index], [field]: value };
        }

        setPaystubs(updatedPaystubs);
    };

    const verifyStub = async () => {
        await persistDraftOtherAmounts();

        if (isSelfEmployedValue) {
            const payload = getSelfEmployedVerifyPayload(taxYear, taxGross);
            context.onVerifyPayStub(
                props.data.id,
                payload.earliestDate,
                payload.latestDate,
                payload.totalGross,
                [],
                false,
                props.data.ssnDecrypted,
                payload.incomeIrsYear,
                payload.incomeIrsYearGross
            );
            return;
        }

        const overview = calculatePaystubOverview(paystubs, props.data);
        const { earliestDate, latestDate } = getPaystubVerifyDates(paystubs, props.data);

        context.onVerifyPayStub(
            props.data.id,
            earliestDate,
            latestDate,
            overview.totalGross,
            paystubs,
            false,
            props.data.ssnDecrypted
        );
    };

    const declineStub = async () => {
        if (!isSelfEmployedValue && paystubs.every(paystub => paystub.reviewRequired !== true)) {
            return;
        }

        await persistDraftOtherAmounts();
        context.onShowDeclineModal(props.data.id, false);
    };

    const overview = calculatePaystubOverview(paystubs, props.data);

    const hasTaxDoc = !!props.data.taxDocumentFile;
    const hasAffidavit = !!props.data.selfEmployedAffirmationFile;
    const viewingTax = isSelfEmployedValue
        && viewerDocs[viewingIndex]?.docType === FileDocumentType.Tax;
    const viewingAffidavit = isSelfEmployedValue
        && viewerDocs[viewingIndex]?.docType === FileDocumentType.DsSelfEmployedAffidavit;
    const seMissingDocs: string[] = [];

    if (isSelfEmployedValue && !hasTaxDoc) {
        seMissingDocs.push('tax document');
    }

    const seCanVerify = seMissingDocs.length === 0 && taxYear > 0 && taxGross > 0;

    const openSelfEmployedDoc = async (docType: number) => {
        const index = viewerDocs.findIndex(doc => doc.docType === docType);

        if (index < 0) {
            return;
        }

        await openViewerAtIndex(index);
    };

    return (
        <div>
            <ConnectedDevMockDocsOffer
                data={props.data}
                downloadFile={(docType, lookupId) => context ? context.downloadFile(docType, lookupId as number) : Promise.resolve()}
                includeWages={true}
                includeReceipts={false}
                forceWageSlots={isSelfEmployedValue ? { forceTax: true } : { forcePaystub: true }}
                onComplete={() => {
                    fileLoadAttemptedForRequestId.current = null;

                    if (context.onRefreshRequest) {
                        context.onRefreshRequest();
                    }
                }}
            />
            <Header data={props.data} showStatus={false} />
            <VerificationStepTracker data={props.data} />

            {isSelfEmployedValue ? (
                <SelfEmployedIncomeSection
                    taxYear={taxYear}
                    taxGross={taxGross}
                    hasTaxDoc={hasTaxDoc}
                    hasAffidavit={hasAffidavit}
                    viewingTax={viewingTax}
                    viewingAffidavit={viewingAffidavit}
                    missingDocs={seMissingDocs}
                    canVerify={seCanVerify}
                    onTaxYearChange={setTaxYear}
                    onTaxGrossChange={setTaxGross}
                    onOpenDoc={openSelfEmployedDoc}
                    onDecline={declineStub}
                    onVerify={verifyStub}
                />
            ) : (
                <WagePaystubIncomeSection
                    overview={overview}
                    isHourly={isHourlyValue}
                    weeklyPayByPayStub={Number(props.data.calculatedWeeklyPayByPayStub) || 0}
                    paystubs={paystubs}
                    hasHeaderPayStubSummary={hasHeaderPayStubSummary}
                    editingIndex={editingIndex}
                    grossFocusIndex={grossFocusIndex}
                    approvedPaystubIds={approvedPaystubIds}
                    isPaystubViewing={isPaystubViewing}
                    onRowClick={handleRowClick}
                    onReviewRequiredChange={handleReviewRequiredChange}
                    onPaystubChange={handlePaystubChange}
                    onModifyClick={handleModifyClick}
                    onGrossFocus={setGrossFocusIndex}
                    onDecline={declineStub}
                    onVerify={verifyStub}
                />
            )}

            <WatrInfoAccordions
                data={props.data}
                isAudit={props.isAudit}
                reimbursements={props.reimbursements}
                allowEditWeekOtherAmount={true}
                draftWeekOtherAmounts={draftOtherAmounts}
                onWeekOtherAmountChange={(weekRequested, otherAmount) => setDraftOtherAmounts(prev => ({ ...prev, [weekRequested]: otherAmount }))}
            />
        </div>
    );
};

export default VerifyStub;
